import { useState } from "react";
import {
  ActionButton,
  Button,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Item,
  Key,
  Menu,
  MenuTrigger,
  Text,
  View,
} from "@adobe/react-spectrum";
import "./Header.css";
import AdobeLogo from "../../assets/adobeLogo.svg";
import AgentStatusChange from "../Dashboard/AgentStatusChange";
import { HELPSUPPORT, SETTINGS } from "../../constants/constants";
import { SETTING_OPTIONS } from "../Dashboard/constants";
import { useSelector } from "react-redux";
import storeRTK from "../../store/storeRTK";
import { setLoader } from "../../actions/common";
import { setCurrentSessionTab } from "../../actions/session";
import { RootState } from "../../store/storeConfig";
import Emitter from "../../emitter";
import { BellIcon, BellIconPlain, ConnectIcon } from "../common/Icons";
import { STOP_SESSION } from "../../constants/events";
import QueueSelection from "../Dashboard/QueueSelection";
import { tabAccessControl } from "../../utils/utils";
import { Tabs } from "../../types/generic.types";
import NotificationsPanel from "../Notifications/NotificationsPanel";

const HeaderComponent = () => {
  // Select only the parts of the state needed for the component
  const agent = useSelector((state: RootState) => state.agent);
  const call = useSelector((state: RootState) => state.call);
  const callback = useSelector((state: RootState) => state.callback);
  const voicemail = useSelector((state: RootState) => state.voicemail);
  const chat = useSelector((state: RootState) => state.chat);
  const sessionState = useSelector((state: RootState) => state.session);
  const notifications = useSelector((state: RootState) => state.notifications as any);
  
  // State for notifications dialog
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const menuOptions =
    agent?.aicConfig?.wfm_scheduling_url_enable === "true"
      ? SETTING_OPTIONS
      : SETTING_OPTIONS.filter((item) => item.id !== 3);

  const onTabChange = (id: string) => {
    storeRTK.dispatch(setCurrentSessionTab({ id }));
  };

  const handleMenuAction = (key: Key) => {
    const actions: Record<string, () => void> = {
      "1": () => {
        setTimeout(() => {
          Emitter.emit(STOP_SESSION);
          storeRTK.dispatch(setLoader(false));
        }, 100);
      },
      "2": () => {
        onTabChange(SETTINGS);
      },
      "3": () => {
        openWfmWindow(agent?.aicConfig?.wfm_scheduling_url);
      },
      "4": () => {
        onTabChange(HELPSUPPORT);
      },
    };

    actions[key]?.();
  };

  const openConnect = () => {
    const url = agent?.aicConfig?.ccp_url.replace("/ccp-v2", "");
    window.open(url, "_blank");
  };

  const openWfmWindow = (url: string) => {
    // Define the features for the new window
    const features =
      "fullscreen=1,toolbar=no,location=no,directories=no,status=no,menubar=no,resizable=no";

    // Open a new window with the specified features
    const newWindow = window.open(url, "_blank", features);

    if (!newWindow) {
      console.error("Failed to open new window");
      return;
    }

    // Add an event listener to handle when the window loads
    newWindow.addEventListener("load", function () {
      try {
        // Check if document is accessible
        if (newWindow.document) {
          // Look for address bar input
          const addressBar =
            newWindow.document.querySelector('input[type="url"]');

          if (addressBar) {
            (addressBar as HTMLInputElement).disabled = true;
          }
        } else {
          console.log(
            "Document not accessible - possible cross-origin restriction"
          );
        }
      } catch (error) {
        console.error("Error accessing new window document:", error);
      }
    });

    // Also try to handle DOMContentLoaded if it hasn't fired yet
    if (newWindow.document.readyState === "loading") {
      newWindow.document.addEventListener("DOMContentLoaded", function () {
        console.log("DOMContentLoaded fired in new window");
      });
    } else {
      console.log(
        "Document already loaded, readyState:",
        newWindow.document.readyState
      );
    }
  };
  return (
    <Flex
      UNSAFE_className="headerWrapper"
      justifyContent={"space-between"}
      alignItems={"center"}
      data-testid="headerMain"
    >
      <Flex alignItems="center" UNSAFE_className="left-content-header">
        <Flex>
          <img src={AdobeLogo} className="adobeLogo" alt="Adobe Logo" />
          <Text UNSAFE_className="aic-logo">iHub</Text>
        </Flex>
      </Flex>
      {agent.firstName && (
        <Flex>
          <Flex UNSAFE_className="versionInfo">
            <Flex>
              <QueueSelection />
            </Flex>
            <Flex UNSAFE_className="agentStatusChangePicker">
              <AgentStatusChange />
            </Flex>
            {/* {sessionState.loggedIn && <MultiInstanceChange />} */}
            <Divider size="S" orientation="vertical" />
            <View>
              {sessionState.loggedIn && (
                <Button
                  height={"20px"}
                  variant="secondary"
                  style="fill"
                  data-testid="open-connect-button"
                  UNSAFE_className="amazon-connect-button"
                  target="_blank"
                  onPress={openConnect}
                  isDisabled={
                    tabAccessControl("amazon_connect_link") !== "true"
                  }
                >
                  <ConnectIcon />
                  <Text
                    marginStart={"2px"}
                    UNSAFE_className="amazon-connect-btn"
                  >
                    Amazon Connect
                  </Text>
                </Button>
              )}
            </View>
            <View>
              <DialogTrigger isOpen={isNotificationsOpen} type="popover" onOpenChange={setIsNotificationsOpen}>
                <ActionButton 
                  aria-label="Notifications"
                  UNSAFE_style={{ 
                    position: 'relative',
                    backgroundColor: 'transparent',
                    border: 'none'
                  }}
                >
                  {notifications?.unreadCount > 0 ? <BellIcon /> : <BellIconPlain />}
                  {/* <BellIcon /> */}
                  {notifications?.unreadCount > 0 && (
                    <View
                      UNSAFE_style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {(notifications?.unreadCount || 0) > 99 ? '99+' : (notifications?.unreadCount || 0)}
                    </View>
                  )}
                </ActionButton>
                    <NotificationsPanel />
              </DialogTrigger>
            </View>

            <Flex alignItems={"center"}>
              {/* <View
                marginEnd={6}
                marginBottom={4}
                UNSAFE_style={{ fontSize: "0.785rem" }}
              >
                <Text>{APP_VERSION ? `v${APP_VERSION}` : ""}</Text>
              </View> */}
              <View UNSAFE_className={"settingsButtonWrapper"}>
                <MenuTrigger>
                  <ActionButton UNSAFE_className={"settingsButton"}>
                    {agent.firstName
                      ? (
                          agent.firstName[0] +
                          (agent.lastName ? agent.lastName[0] : "")
                        ).toUpperCase()
                      : "NA"}
                  </ActionButton>
                  <Menu
                    onAction={(key: Key) => handleMenuAction(key)}
                    disabledKeys={
                      (call.id && call.self?.id) ||
                      (callback.id && callback.self?.id) ||
                      (voicemail.id && voicemail.self?.id) ||
                      chat.chats.length
                        ? ["1"]
                        : []
                    }
                    UNSAFE_className="menu-logout-btn"
                  >
                    {menuOptions.map((item: Tabs) => (
                      <Item key={item.id}>
                        {/* <Text UNSAFE_className="menu-logout-btn"> */}
                        {item.name}
                        {/* </Text> */}
                      </Item>
                    ))}
                  </Menu>
                </MenuTrigger>
              </View>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default HeaderComponent;
